import { useQuery, type UseQueryResult } from 'react-query'

import type { components } from '@23/openapi'

import queryClient from '../../queryClient'
import { publicApiRequest } from '../../global/api/api'
import {
  type BrandTypography,
  parseTypography,
} from '../models/BrandTypography'

type BrandTokenType = components['schemas']['BrandStudioTokenType']
type BrandToken = components['schemas']['BrandStudioTokenResponse']

const BRAND_STUDIO_QUERY_KEY = 'brand-studio'

const queryKeys = {
  list: (type: BrandTokenType) => [BRAND_STUDIO_QUERY_KEY, type],
  item: (id: string) => [BRAND_STUDIO_QUERY_KEY, id],
}

const useListBrandTokensQuery = <T extends BrandTokenType>(
  type: T,
  isBrandStudioEnabled: boolean = true
): UseQueryResult<
  ((T extends 'typography'
    ? BrandToken & { value: BrandTypography; string: string }
    : BrandToken) & { type: T })[]
> => {
  return useQuery({
    queryKey: queryKeys.list(type),
    queryFn: async () => {
      const { data } = await publicApiRequest('brand-studio/tokens', 'get', {
        type,
      })

      const cleanData = data.map((token) => {
        if (token.type === 'typography')
          return {
            ...token,
            value: parseTypography(token.value),
            string: token.value,
          }
        return token
      })

      cleanData.forEach((token) => {
        queryClient.setQueryData(queryKeys.item(token.id), token)
      })
      return cleanData
    },
    enabled: isBrandStudioEnabled,
    staleTime: 10 * 60 * 1e3,
  })
}

const useBrandTokenQuery = (uuid?: string) =>
  useQuery<BrandToken>({
    // oxlint-disable-next-line typescript/no-non-null-assertion
    queryKey: queryKeys.item(uuid!),
    enabled: !!uuid,
  })

const useBrandTypographyTokenQuery = (uuid?: string) =>
  useQuery<
    Omit<BrandToken, 'value'> & { value: BrandTypography; string: string }
  >({
    // oxlint-disable-next-line typescript/no-non-null-assertion
    queryKey: queryKeys.item(uuid!),
    enabled: !!uuid,
  })

export {
  type BrandTokenType,
  useListBrandTokensQuery,
  useBrandTokenQuery,
  useBrandTypographyTokenQuery,
}
